import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { recordingApi, type PaginatedRecordingsResponse } from "@/lib/api";
import type { Recording } from "@/types";
import { useOrganizationStore } from "@/stores/organizationStore";
import type { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { DEFAULT_PAGE_SIZE } from "@/lib/constants";

interface UseRecordingsOptions {
  enabled?: boolean;
  artistIds?: string[];
  releaseIds?: string[];
  dateRange?: DateRange;
  pageSize?: number;
  ordering?: string;
  page?: number;
  search?: string;
  isrc?: string;
  hasIsrc?: boolean;
  excludeReleaseId?: string;
  noContract?: boolean;
  prefetchNextPage?: boolean;
}

interface UseRecordingsReturn {
  recordings: Recording[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

// Helper function to create normalized query keys
const createRecordingsQueryKey = (options: {
  labelId: string | undefined;
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  isrc?: string;
  hasIsrc?: boolean;
  excludeReleaseId?: string;
  noContract?: boolean;
  artistIds?: string[];
  releaseIds?: string[];
  dateFrom?: Date;
  dateTo?: Date;
}) => {
  const key: (string | number)[] = ["recordings"];

  // Always include labelId first
  if (options.labelId) {
    key.push("label", options.labelId);
  }

  // Add pagination params
  if (options.page && options.page > 1) {
    key.push("page", options.page);
  }

  if (options.pageSize && options.pageSize !== DEFAULT_PAGE_SIZE) {
    key.push("pageSize", options.pageSize);
  }

  if (options.ordering) {
    key.push("ordering", options.ordering);
  }

  // Add search filter
  const normalizedSearch = options.search?.trim() || null;
  if (normalizedSearch) {
    key.push("search", normalizedSearch);
  }

  // Add isrc filter
  const normalizedIsrc = options.isrc?.trim() || null;
  if (normalizedIsrc) {
    key.push("isrc", normalizedIsrc.toUpperCase());
  }

  if (options.hasIsrc !== undefined) {
    key.push("hasIsrc", String(options.hasIsrc));
  }

  if (options.excludeReleaseId) {
    key.push("excludeReleaseId", options.excludeReleaseId);
  }

  if (options.noContract !== undefined) {
    key.push("noContract", String(options.noContract));
  }

  // Normalize and sort arrays for consistent caching
  if (options.artistIds?.length) {
    const sortedArtistIds = [...options.artistIds].sort();
    key.push("artistIds", sortedArtistIds.join(","));
  }

  if (options.releaseIds?.length) {
    const sortedReleaseIds = [...options.releaseIds].sort();
    key.push("releaseIds", sortedReleaseIds.join(","));
  }

  // Add date range
  if (options.dateFrom) {
    key.push("dateFrom", format(options.dateFrom, "yyyy-MM-dd"));
  }

  if (options.dateTo) {
    key.push("dateTo", format(options.dateTo, "yyyy-MM-dd"));
  }

  return key;
};

export const useRecordings = ({
  enabled = true,
  artistIds = [],
  releaseIds = [],
  dateRange,
  pageSize,
  ordering,
  page,
  search,
  isrc,
  hasIsrc,
  excludeReleaseId,
  noContract,
  prefetchNextPage = true,
}: UseRecordingsOptions = {}): UseRecordingsReturn => {
  const { currentLabel } = useOrganizationStore();

  const currentPage = page ?? 1;

  const buildQueryKey = (pageNum: number) =>
    createRecordingsQueryKey({
      labelId: currentLabel?.id,
      page: pageNum,
      pageSize,
      ordering,
      search,
      isrc,
      hasIsrc,
      excludeReleaseId,
      noContract,
      artistIds,
      releaseIds,
      dateFrom: dateRange?.from,
      dateTo: dateRange?.to,
    });

  const buildQueryFn = (pageNum: number) => async (): Promise<PaginatedRecordingsResponse> => {
    return await recordingApi.getRecordings({
      label_id: currentLabel?.id,
      page: pageNum,
      page_size: pageSize,
      ordering,
      contributor_ids: artistIds.length > 0 ? artistIds.join(",") : undefined,
      release_ids: releaseIds.length > 0 ? releaseIds.join(",") : undefined,
      q: search || undefined,
      isrc: isrc || undefined,
      has_isrc: hasIsrc,
      exclude_release_id: excludeReleaseId,
      no_contract: noContract,
      release_date_after: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined,
      release_date_before: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined,
    });
  };

  const { data, isLoading, isError, error, isSuccess, refetch, isPlaceholderData } = useQuery({
    queryKey: buildQueryKey(currentPage),
    queryFn: buildQueryFn(currentPage),
    enabled: enabled && !!currentLabel?.id,
    placeholderData: keepPreviousData,
  });

  // Prefetch next page in parallel (only when current data is fresh, not placeholder)
  useQuery({
    queryKey: buildQueryKey(currentPage + 1),
    queryFn: buildQueryFn(currentPage + 1),
    enabled: prefetchNextPage && enabled && !!currentLabel?.id && !!data?.next && !isPlaceholderData,
    staleTime: Infinity,
  });

  return {
    recordings: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
